import apiClient from '../utils/apiClient';
import { API_ENDPOINTS } from '../config/api';
import { ApiService } from './apiService';
import { ApiError, PaginatedResponse } from '../types/api';
import {
  AdminUserInvoiceRow,
  AdminUserInvoicesQuery,
  DownloadInvoiceQuery,
  InvoiceConnectsSummary,
  InvoiceFilterQueryParams,
  InvoicePurchaseType,
  InvoiceSummary,
  SendInvoiceRequest,
} from '../types/userInvoice';
import { appendQueryString } from '../utils/queryParams';
import { extractFilenameFromDisposition, triggerBlobDownload } from '../utils/blobDownload';

export { triggerBlobDownload };

type RawInvoiceRow = Record<string, unknown>;

const toStringValue = (value: unknown, fallback = ''): string =>
  value === undefined || value === null ? fallback : String(value);

const toNumberValue = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toOptionalInvoiceNote = (value: unknown): string | null | undefined => {
  if (value === undefined || value === null) return undefined;
  const str = String(value).trim();
  return str || null;
};

const parseBlobErrorMessage = async (blob: Blob): Promise<string | null> => {
  try {
    const text = await blob.text();
    const parsed = JSON.parse(text) as Record<string, unknown>;
    if (typeof parsed.message === 'string' && parsed.message.trim()) {
      return parsed.message;
    }
    if (typeof parsed.error === 'string' && parsed.error.trim()) {
      return parsed.error;
    }
  } catch {
    return null;
  }
  return null;
};

const createDownloadError = (message: string, status?: number): ApiError => ({
  message,
  status,
});

const buildInvoiceFilterUrl = (
  path: string,
  params?: InvoiceFilterQueryParams
): string =>
  appendQueryString(path, {
    status: params?.status,
    timePeriod: params?.timePeriod,
    durationMonths: params?.durationMonths,
  });

const normalizeDownloadError = async (
  error: unknown,
  fallback = 'Failed to download invoice PDF.'
): Promise<ApiError> => {
  if (error && typeof error === 'object' && 'message' in error) {
    const apiError = error as ApiError;
    if (typeof apiError.message === 'string' && apiError.message.trim()) {
      return apiError;
    }
  }

  const axiosError = error as {
    response?: {
      status?: number;
      data?: Blob;
      headers?: Record<string, string>;
    };
  };

  if (axiosError.response?.data instanceof Blob) {
    const message =
      (await parseBlobErrorMessage(axiosError.response.data)) ?? fallback;
    return createDownloadError(message, axiosError.response.status);
  }

  return createDownloadError(fallback);
};

/** Map camelCase or snake_case invoice rows to AdminUserInvoiceRow. */
export const normalizeAdminUserInvoiceRow = (raw: RawInvoiceRow): AdminUserInvoiceRow => {
  const type = (raw.type ?? 'plan') as InvoicePurchaseType;
  const userPlanId = toNumberValue(
    raw.user_plan_id ?? raw.userPlanId ?? (type === 'plan' ? raw.reference_id ?? raw.referenceId ?? raw.id : undefined),
    0
  );
  const referenceId = toNumberValue(
    raw.reference_id ?? raw.referenceId ?? raw.user_plan_id ?? raw.userPlanId ?? raw.report_id ?? raw.reportId ?? raw.id,
    0
  );

  return {
    invoice_number: (raw.invoice_number ?? raw.invoiceNumber ?? null) as string | null,
    date: toStringValue(raw.date ?? raw.issued_at ?? raw.issuedAt ?? raw.created_at ?? raw.createdAt),
    plan_name: toStringValue(raw.plan_name ?? raw.planName ?? raw.name),
    amount: toNumberValue(raw.amount ?? raw.total_amount ?? raw.totalAmount),
    currency: toStringValue(raw.currency, 'INR'),
    status: (raw.status ?? 'PAID') as 'PAID',
    type,
    reference_id: referenceId,
    user_plan_id: type === 'plan' ? userPlanId || referenceId : undefined,
    has_pdf: Boolean(raw.has_pdf ?? raw.hasPdf ?? raw.invoice_number ?? raw.invoiceNumber),
    note_from_admin: toOptionalInvoiceNote(raw.note_from_admin ?? raw.noteFromAdmin),
  };
};

export class UserInvoiceService extends ApiService {
  async listInvoices(
    clientUserId: string,
    params?: AdminUserInvoicesQuery
  ): Promise<PaginatedResponse<AdminUserInvoiceRow>> {
    const url = appendQueryString(API_ENDPOINTS.users.invoices(clientUserId), {
      page: params?.page ?? 1,
      limit: params?.limit ?? 20,
    });
    const response = await this.get<PaginatedResponse<RawInvoiceRow>>(url);

    return {
      ...response,
      data: (response.data ?? []).map(normalizeAdminUserInvoiceRow),
    };
  }

  async downloadInvoicePdf(
    type: InvoicePurchaseType,
    id: number
  ): Promise<{ blob: Blob; filename: string }> {
    const url = appendQueryString(API_ENDPOINTS.invoices.download, {
      type,
      id,
    });

    try {
      const response = await apiClient.get<Blob>(url, { responseType: 'blob' });
      const blob = response.data;
      const contentType = String(response.headers['content-type'] ?? '').toLowerCase();

      if (contentType.includes('application/json') || contentType.includes('text/plain')) {
        const message =
          (await parseBlobErrorMessage(blob)) ?? 'Failed to download invoice PDF.';
        throw createDownloadError(message, response.status);
      }

      const disposition = String(response.headers['content-disposition'] ?? '');
      const filename = extractFilenameFromDisposition(disposition) ?? `invoice-${id}.pdf`;

      return { blob, filename };
    } catch (error) {
      throw await normalizeDownloadError(error);
    }
  }

  async downloadPlanInvoice(userPlanId: number): Promise<{ blob: Blob; filename: string }> {
    return this.downloadInvoicePdf('plan', userPlanId);
  }

  async getSummary(params?: InvoiceFilterQueryParams): Promise<InvoiceSummary> {
    const url = buildInvoiceFilterUrl(API_ENDPOINTS.invoices.summary, params);
    const response = await this.get<RawInvoiceRow>(url);
    return normalizeInvoiceSummary(response);
  }

  async exportReport(params?: InvoiceFilterQueryParams): Promise<void> {
    const url = buildInvoiceFilterUrl(API_ENDPOINTS.invoices.export, params);

    try {
      const response = await apiClient.get<Blob>(url, { responseType: 'blob' });
      const blob = response.data;
      const contentType = String(response.headers['content-type'] ?? '').toLowerCase();

      if (contentType.includes('application/json') || contentType.includes('text/plain')) {
        const message =
          (await parseBlobErrorMessage(blob)) ?? 'Failed to export invoice report.';
        throw createDownloadError(message, response.status);
      }

      const disposition = String(response.headers['content-disposition'] ?? '');
      const filename =
        extractFilenameFromDisposition(disposition)
        ?? `invoices-export-${new Date().toISOString().slice(0, 10)}.xlsx`;

      triggerBlobDownload(blob, filename);
    } catch (error) {
      throw await normalizeDownloadError(error, 'Failed to export invoice report.');
    }
  }

  async sendInvoice(payload: SendInvoiceRequest): Promise<void> {
    await this.post<void>(API_ENDPOINTS.invoices.send, payload);
  }
}

export const userInvoiceService = new UserInvoiceService();

export const formatInvoiceDate = (iso: string): string => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
};

export const formatInvoiceAmount = (amount: number, currency: string): string => {
  const locale = currency === 'INR' ? 'en-IN' : 'en-CA';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency || 'INR',
    minimumFractionDigits: 2,
  }).format(amount);
};

export const buildDownloadQuery = (row: AdminUserInvoiceRow): DownloadInvoiceQuery => ({
  type: row.type,
  id: row.type === 'plan' ? (row.user_plan_id ?? row.reference_id) : row.reference_id,
});

export const getInvoiceDownloadId = (row: AdminUserInvoiceRow): number =>
  row.type === 'plan' ? (row.user_plan_id ?? row.reference_id) : row.reference_id;

const normalizeConnectsSummary = (raw: RawInvoiceRow): InvoiceConnectsSummary => {
  const nested = raw.connects_used ?? raw.connectsUsed;

  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const connects = nested as Record<string, unknown>;
    return {
      used: toNumberValue(connects.used),
      total: toNumberValue(connects.total),
      utilizationPercent: toNumberValue(
        connects.utilizationPercent ?? connects.utilization_percent
      ),
    };
  }

  const used = toNumberValue(nested);
  const total = toNumberValue(raw.total_connects ?? raw.totalConnects);
  return {
    used,
    total,
    utilizationPercent: total > 0 ? Math.round((used / total) * 100) : 0,
  };
};

export const normalizeInvoiceSummary = (raw: RawInvoiceRow): InvoiceSummary => ({
  totalRevenue: toNumberValue(raw.total_revenue ?? raw.totalRevenue),
  currency: toStringValue(raw.currency, 'INR'),
  activePlans: toNumberValue(raw.active_plans ?? raw.activePlans),
  connectsUsed: normalizeConnectsSummary(raw),
});

export const formatInvoiceId = (row: AdminUserInvoiceRow): string =>
  row.invoice_number?.trim() || 'Pending';

export const formatInvoicePlanName = (row: AdminUserInvoiceRow): string =>
  row.plan_name?.trim() || '—';
